import React, { useCallback, useEffect, useState } from 'react'
import {
  backendService,
  type MarketplaceBid,
  type MarketplaceFarm,
  type MarketplaceJob,
  type MarketplaceJobCreateRequest,
  type MarketplaceNotification,
  type MarketplaceQuote,
  type MarketplaceStlOption
} from '../../services/backendService'
import type { MarketplaceJobDraft, MarketplaceLaunchContext, MarketplaceTabId } from '../../types/windows'
import { JobCreationForm } from './components/JobCreationForm'
import { JobList } from './components/JobList'
import { JobDetails } from './components/JobDetails'
import { MarketplaceInbox } from './components/MarketplaceInbox'
import { FarmAIChat } from './components/FarmAIChat'
import './DoctorMarketplace.css'

interface DoctorMarketplaceProps {
  activeTab: MarketplaceTabId
  launchContext?: MarketplaceLaunchContext
  onUnreadCountChange?: (count: number) => void
  onOpenJob?: (jobId: string) => void
  pendingJobId?: string | null
  onPendingJobConsumed?: () => void
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

export const DoctorMarketplace: React.FC<DoctorMarketplaceProps> = ({ activeTab, launchContext, onUnreadCountChange, onOpenJob, pendingJobId, onPendingJobConsumed }) => {
  const [selectedJob, setSelectedJob] = useState<MarketplaceJob | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [jobDraft, setJobDraft] = useState<MarketplaceJobDraft | undefined>()
  const [jobs, setJobs] = useState<MarketplaceJob[]>([])
  const [farms, setFarms] = useState<MarketplaceFarm[]>([])
  const [jobBids, setJobBids] = useState<MarketplaceBid[]>([])
  const [jobQuotes, setJobQuotes] = useState<MarketplaceQuote[]>([])
  const [stlOptions, setStlOptions] = useState<MarketplaceStlOption[]>([])
  const [notifications, setNotifications] = useState<MarketplaceNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingFarms, setIsLoadingFarms] = useState(false)
  const [isLoadingOffers, setIsLoadingOffers] = useState(false)
  const [isLoadingStlOptions, setIsLoadingStlOptions] = useState(false)
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false)
  const [isSubmittingJob, setIsSubmittingJob] = useState(false)
  const [selectingOfferId, setSelectingOfferId] = useState<string | null>(null)
  const [confirmingDeliveryJobId, setConfirmingDeliveryJobId] = useState<string | null>(null)
  const [supportsDoctorDeliveryConfirmation, setSupportsDoctorDeliveryConfirmation] = useState(true)
  const [markingNotificationId, setMarkingNotificationId] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [jobFormError, setJobFormError] = useState<string | null>(null)
  const [notificationError, setNotificationError] = useState<string | null>(null)
  const [farmSortBy, setFarmSortBy] = useState<'rating' | 'reviews' | 'turnaround' | 'name'>('rating')
  const [farmMaterialFilter, setFarmMaterialFilter] = useState('')
  const [farmLocationFilter, setFarmLocationFilter] = useState('')
  const [farmSearchInput, setFarmSearchInput] = useState('')
  const [farmSearchTerm, setFarmSearchTerm] = useState('')
  const [farmViewMode, setFarmViewMode] = useState<'card' | 'list'>('card')

  const loadJobs = async () => {
    setIsLoadingJobs(true)
    setPageError(null)

    try {
      const nextJobs = await backendService.listMarketplaceJobs()
      setJobs(nextJobs)
      setSelectedJob(current => current ? nextJobs.find(job => job.id === current.id) || null : null)
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load marketplace jobs'))
    } finally {
      setIsLoadingJobs(false)
    }
  }

  const loadFarms = async ({ background = false }: { background?: boolean } = {}) => {
    if (!background) {
      setIsLoadingFarms(true)
      setPageError(null)
    }

    try {
      setFarms(await backendService.listMarketplaceFarms())
    } catch (error) {
      if (!background) {
        setPageError(getErrorMessage(error, 'Failed to load marketplace farms'))
      }
    } finally {
      if (!background) {
        setIsLoadingFarms(false)
      }
    }
  }

  const loadJobOffers = async (jobId: string) => {
    setIsLoadingOffers(true)
    setDetailsError(null)

    try {
      const offers = await backendService.getMarketplaceJobOffers(jobId)
      setJobBids(offers.bids)
      setJobQuotes(offers.quotes)
    } catch (error) {
      setDetailsError(getErrorMessage(error, 'Failed to load job offers'))
    } finally {
      setIsLoadingOffers(false)
    }
  }

  const loadStlOptions = useCallback(async () => {
    setIsLoadingStlOptions(true)
    setJobFormError(null)

    try {
      setStlOptions(await backendService.listMarketplaceStlOptions())
    } catch (error) {
      setJobFormError(getErrorMessage(error, 'Failed to load STL options'))
    } finally {
      setIsLoadingStlOptions(false)
    }
  }, [])

  const loadNotifications = async () => {
    setIsLoadingNotifications(true)
    setNotificationError(null)

    try {
      const response = await backendService.listMarketplaceNotifications()
      setNotifications(response.notifications)
      setUnreadCount(response.unreadCount)
    } catch (error) {
      setNotificationError(getErrorMessage(error, 'Failed to load marketplace inbox'))
    } finally {
      setIsLoadingNotifications(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'inbox') {
      void loadNotifications()
      return
    }

    if (activeTab === 'my-jobs') {
      void loadJobs()
      void loadFarms({ background: true })
      return
    }

    if (activeTab === 'girasoles-assistant') {
      return
    }

    void loadFarms()
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'my-jobs' && selectedJob) {
      if (farms.length === 0) {
        void loadFarms({ background: true })
      }

      void loadJobOffers(selectedJob.id)
      return
    }

    setJobBids([])
    setJobQuotes([])
    setDetailsError(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedJob?.id])

  useEffect(() => {
    if (showCreateForm) {
      void loadStlOptions()
    }
  }, [showCreateForm, loadStlOptions])

  useEffect(() => {
    onUnreadCountChange?.(unreadCount)
  }, [unreadCount, onUnreadCountChange])

  useEffect(() => {
    if (!pendingJobId) return
    const match = jobs.find(job => job.id === pendingJobId)
    if (match) {
      setSelectedJob(match)
      onPendingJobConsumed?.()
    }
  }, [pendingJobId, jobs, onPendingJobConsumed])

  useEffect(() => {
    if (activeTab !== 'my-jobs' || !launchContext?.openCreateJob) {
      return
    }

    setSelectedJob(null)
    setPageError(null)
    setJobFormError(null)
    setJobDraft(launchContext.draftJob)
    setShowCreateForm(true)

    void loadStlOptions()
  }, [activeTab, launchContext, loadStlOptions])

  const handleOpenCreateForm = () => {
    setSelectedJob(null)
    setJobFormError(null)
    setJobDraft(undefined)
    setShowCreateForm(true)
  }

  const handleCloseCreateForm = () => {
    setShowCreateForm(false)
    setJobDraft(undefined)
    setJobFormError(null)
  }

  const handleMarkNotificationRead = async (notificationId: string) => {
    setMarkingNotificationId(notificationId)
    setNotificationError(null)

    try {
      const updatedNotification = await backendService.markMarketplaceNotificationRead(notificationId)
      setNotifications(current => current.map(notification => (
        notification.id === notificationId ? updatedNotification : notification
      )))
      setUnreadCount(current => Math.max(0, current - 1))
    } catch (error) {
      setNotificationError(getErrorMessage(error, 'Failed to mark notification as read'))
    } finally {
      setMarkingNotificationId(null)
    }
  }

  const handleCreateJob = async (jobData: MarketplaceJobCreateRequest) => {
    setIsSubmittingJob(true)
    setJobFormError(null)

    try {
      const createdJob = await backendService.createMarketplaceJob(jobData)
      setJobs(current => [createdJob, ...current])
      setSelectedJob(null)
      setShowCreateForm(false)
      setJobDraft(undefined)
      setPageError(null)
    } catch (error) {
      setJobFormError(getErrorMessage(error, 'Failed to create marketplace job'))
    } finally {
      setIsSubmittingJob(false)
    }
  }

  const handleSelectOffer = async (selection: { bidId?: string; quoteId?: string; paymentMethod?: 'card' }) => {
    if (!selectedJob) {
      return
    }

    const nextOfferId = selection.bidId || selection.quoteId || null
    setSelectingOfferId(nextOfferId)
    setDetailsError(null)

    try {
      await backendService.selectMarketplaceFarm(selectedJob.id, selection)
      await Promise.all([loadJobs(), loadJobOffers(selectedJob.id)])
    } catch (error) {
      setDetailsError(getErrorMessage(error, 'Failed to select marketplace farm'))
    } finally {
      setSelectingOfferId(null)
    }
  }

  const handleConfirmDelivered = async (jobId: string) => {
    setConfirmingDeliveryJobId(jobId)
    setDetailsError(null)

    try {
      const response = await backendService.confirmMarketplaceJobDelivered(jobId)
      setJobs(current => current.map(job => (job.id === jobId ? response.job : job)))
      setSelectedJob(current => (current && current.id === jobId ? response.job : current))
      setSupportsDoctorDeliveryConfirmation(true)
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to confirm delivery')

      if (errorMessage.toLowerCase().includes('not found')) {
        setSupportsDoctorDeliveryConfirmation(false)
        setDetailsError('Doctor-side delivery confirmation is not available on this backend deployment yet. Please deploy the backend that includes /api/marketplace/jobs/{jobId}/confirm-delivered.')
      } else {
        setDetailsError(errorMessage)
      }
    } finally {
      setConfirmingDeliveryJobId(null)
    }
  }

  if (activeTab === 'inbox') {
    return (
      <div className="doctor-marketplace">
        <MarketplaceInbox
          title="Inbox"
          notifications={notifications}
          unreadCount={unreadCount}
          isLoading={isLoadingNotifications}
          errorMessage={notificationError}
          markingNotificationId={markingNotificationId}
          onRefresh={loadNotifications}
          onMarkAsRead={handleMarkNotificationRead}
          onOpenJob={onOpenJob}
        />
      </div>
    )
  }

  if (activeTab === 'my-jobs') {
    return (
      <div className="doctor-marketplace">
        <div className="doctor-header">
          <h2>My Print Jobs</h2>
          <button
            className="btn-primary btn-small"
            onClick={() => {
              if (showCreateForm) {
                handleCloseCreateForm()
                return
              }

              handleOpenCreateForm()
            }}
          >
            {showCreateForm ? '✕ Cancel' : '+ New Job'}
          </button>
        </div>

        {showCreateForm && (
          <JobCreationForm
            stlOptions={stlOptions}
            initialValues={jobDraft}
            isLoadingStlOptions={isLoadingStlOptions}
            isSubmitting={isSubmittingJob}
            errorMessage={jobFormError}
            onSubmit={handleCreateJob}
            onCancel={handleCloseCreateForm}
          />
        )}

        {pageError && !selectedJob && <div className="marketplace-error-banner">{pageError}</div>}

        {selectedJob ? (
          <JobDetails
            job={selectedJob}
            farms={farms}
            bids={jobBids}
            quotes={jobQuotes}
            isLoadingOffers={isLoadingOffers}
            actionError={detailsError}
            selectingOfferId={selectingOfferId}
            isConfirmingDelivery={confirmingDeliveryJobId === selectedJob.id}
            supportsDoctorDeliveryConfirmation={supportsDoctorDeliveryConfirmation}
            onBack={() => setSelectedJob(null)}
            onSelectBid={(bidId, paymentMethod) => handleSelectOffer({ bidId, paymentMethod })}
            onSelectQuote={(quoteId, paymentMethod) => handleSelectOffer({ quoteId, paymentMethod })}
            onConfirmDelivered={handleConfirmDelivered}
          />
        ) : isLoadingJobs ? (
          <div className="placeholder">
            <p>Loading marketplace jobs...</p>
          </div>
        ) : (
          <JobList jobs={jobs} onSelectJob={setSelectedJob} />
        )}
      </div>
    )
  }

  if (activeTab === 'girasoles-assistant') {
    return (
      <div className="doctor-marketplace">
        <FarmAIChat />
      </div>
    )
  }

  const filteredFarms = farms
    .filter(farm => {
      const matchesMaterial = !farmMaterialFilter || farm.materials.includes(farmMaterialFilter)
      const matchesLocation = !farmLocationFilter || farm.location === farmLocationFilter
      const searchHaystack = `${farm.name} ${farm.location} ${farm.materials.join(' ')} ${farm.description}`.toLowerCase()
      const matchesSearch = !farmSearchTerm || searchHaystack.includes(farmSearchTerm)

      return matchesMaterial && matchesLocation && matchesSearch
    })
    .sort((left, right) => {
      if (farmSortBy === 'reviews') {
        return right.reviewCount - left.reviewCount
      }
      if (farmSortBy === 'turnaround') {
        const leftDays = typeof left.averageTurnaroundDays === 'number' ? left.averageTurnaroundDays : Number.MAX_SAFE_INTEGER
        const rightDays = typeof right.averageTurnaroundDays === 'number' ? right.averageTurnaroundDays : Number.MAX_SAFE_INTEGER
        return leftDays - rightDays
      }
      if (farmSortBy === 'name') {
        return left.name.localeCompare(right.name)
      }
      return right.rating - left.rating
    })

  return (
    <div className="doctor-marketplace">
      <div className="doctor-header">
        <h2>Browse Available Farms</h2>
        <div className="marketplace-view-toggle" aria-label="Farms view mode">
          <button
            type="button"
            className={`marketplace-view-toggle-btn ${farmViewMode === 'card' ? 'is-active' : ''}`}
            onClick={() => setFarmViewMode('card')}
          >
            Cards
          </button>
          <button
            type="button"
            className={`marketplace-view-toggle-btn ${farmViewMode === 'list' ? 'is-active' : ''}`}
            onClick={() => setFarmViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      <div className="farm-filters" aria-label="Farm sorting and filters">
        <div className="farm-filter-group">
          <label htmlFor="farm-sort">Sort by</label>
          <select id="farm-sort" value={farmSortBy} onChange={(event) => setFarmSortBy(event.target.value as 'rating' | 'reviews' | 'turnaround' | 'name')}>
            <option value="rating">Highest rating</option>
            <option value="reviews">Most reviews</option>
            <option value="turnaround">Fastest turnaround</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
        <div className="farm-filter-group">
          <label htmlFor="farm-material">Material</label>
          <select id="farm-material" value={farmMaterialFilter} onChange={(event) => setFarmMaterialFilter(event.target.value)}>
            <option value="">All materials</option>
            {Array.from(new Set(farms.flatMap(farm => farm.materials))).sort().map(material => (
              <option key={material} value={material}>{material}</option>
            ))}
          </select>
        </div>
        <div className="farm-filter-group">
          <label htmlFor="farm-location">Location</label>
          <select id="farm-location" value={farmLocationFilter} onChange={(event) => setFarmLocationFilter(event.target.value)}>
            <option value="">All locations</option>
            {Array.from(new Set(farms.map(farm => farm.location))).sort().map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>
        <div className="farm-filter-group farm-search-group">
          <label htmlFor="farm-search">Search</label>
          <input
            id="farm-search"
            type="text"
            value={farmSearchInput}
            placeholder="Farm name, material, location"
            onChange={(event) => setFarmSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setFarmSearchTerm(farmSearchInput.trim().toLowerCase())
              }
            }}
          />
          <button
            type="button"
            className="farm-search-button"
            onClick={() => setFarmSearchTerm(farmSearchInput.trim().toLowerCase())}
          >
            Search
          </button>
        </div>
      </div>

      {pageError && <div className="marketplace-error-banner">{pageError}</div>}

      {isLoadingFarms ? (
        <div className="placeholder">
          <p>Loading marketplace farms...</p>
        </div>
      ) : farms.length === 0 ? (
        <div className="placeholder">
          <p>No farms are available right now.</p>
        </div>
      ) : filteredFarms.length === 0 ? (
        <div className="placeholder">
          <p>No farms match the current filters.</p>
        </div>
      ) : farmViewMode === 'list' ? (
        <div className="farms-table-shell">
          <div className="farms-table" role="table" aria-label="Marketplace farms list">
            <div className="farms-table-row farms-table-head" role="row">
              <div className="farms-table-cell farm-col-name" role="columnheader">Farm</div>
              <div className="farms-table-cell farm-col-location" role="columnheader">Location</div>
              <div className="farms-table-cell farm-col-reviews" role="columnheader">Reviews</div>
              <div className="farms-table-cell farm-col-materials" role="columnheader">Materials</div>
              <div className="farms-table-cell farm-col-quality" role="columnheader">Quality</div>
              <div className="farms-table-cell farm-col-min" role="columnheader">Min Order</div>
              <div className="farms-table-cell farm-col-turnaround" role="columnheader">Turnaround</div>
              <div className="farms-table-cell farm-col-action" role="columnheader">Action</div>
            </div>

            {filteredFarms.map(farm => (
              <div key={farm.id} className="farms-table-row" role="row">
                <div className="farms-table-cell farm-col-name" role="cell">
                  <div className="farm-table-name">
                    <strong>{farm.name}</strong>
                    {farm.verified && <span className="verified-badge">✓ Verified</span>}
                  </div>
                </div>
                <div className="farms-table-cell farm-col-location" role="cell">{farm.location}</div>
                <div className="farms-table-cell farm-col-reviews" role="cell">
                  <span className="farm-inline-rating">
                    <span className="stars">{'★'.repeat(Math.floor(farm.rating))}{'☆'.repeat(5 - Math.floor(farm.rating))}</span>
                    <span className="rating-value">{farm.rating.toFixed(1)}</span>
                    <span className="review-count">({farm.reviewCount})</span>
                  </span>
                </div>
                <div className="farms-table-cell farm-col-materials" role="cell">{farm.materials.join('/')}</div>
                <div className="farms-table-cell farm-col-quality" role="cell">{farm.maxQuality}</div>
                <div className="farms-table-cell farm-col-min" role="cell">{typeof farm.minimumOrderValue === 'number' ? `$${farm.minimumOrderValue.toFixed(2)}` : 'N/A'}</div>
                <div className="farms-table-cell farm-col-turnaround" role="cell">{typeof farm.averageTurnaroundDays === 'number' ? `~${farm.averageTurnaroundDays}d` : 'N/A'}</div>
                <div className="farms-table-cell farm-col-action" role="cell">
                  <button className="btn-contact farm-table-action" type="button" disabled>
                    Ready
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className={`farms-grid ${farmViewMode !== 'card' ? 'farms-list-view' : ''}`}>
        {filteredFarms.map(farm => (
          <div key={farm.id} className={`farm-card ${farmViewMode !== 'card' ? 'is-list is-compact' : ''}`.trim()}>
            <div className="farm-header">
              <div className="farm-header-main">
                <h3>{farm.name}</h3>
                <span className="farm-header-inline-details">
                  <span>{farm.location}</span>
                  <span className="farm-inline-rating">
                    <span className="stars">{'★'.repeat(Math.floor(farm.rating))}{'☆'.repeat(5 - Math.floor(farm.rating))}</span>
                    <span className="rating-value">{farm.rating.toFixed(1)}</span>
                    <span className="review-count">({farm.reviewCount})</span>
                  </span>
                  <span>{farm.materials.join('/')}</span>
                  <span>{farm.maxQuality}</span>
                  <span>{typeof farm.minimumOrderValue === 'number' ? `$${farm.minimumOrderValue.toFixed(2)}` : 'N/A'}</span>
                  <span>{typeof farm.averageTurnaroundDays === 'number' ? `~${farm.averageTurnaroundDays}d` : 'N/A'}</span>
                </span>
              </div>
              {farm.verified && <span className="verified-badge">✓ Verified</span>}
            </div>
            <div className="farm-location">{farm.location}</div>
            <div className="farm-rating">
              <span className="stars">{'★'.repeat(Math.floor(farm.rating))}{'☆'.repeat(5 - Math.floor(farm.rating))}</span>
              <span className="rating-value">{farm.rating.toFixed(1)}</span>
              <span className="review-count">({farm.reviewCount} reviews)</span>
            </div>
            <div className="farm-description">{farm.description}</div>
            <div className="farm-specs">
              <div className="spec">
                <strong>Materials:</strong> {farm.materials.join(', ')}
              </div>
              <div className="spec">
                <strong>Quality:</strong> Up to {farm.maxQuality}
              </div>
              <div className="spec">
                <strong>Minimum Order:</strong>{' '}
                {typeof farm.minimumOrderValue === 'number' ? `$${farm.minimumOrderValue.toFixed(2)}` : 'Not specified'}
              </div>
              <div className="spec spec-turnaround">
                <strong>Turnaround:</strong>
                <span className="spec-value">{typeof farm.averageTurnaroundDays === 'number' ? `~${farm.averageTurnaroundDays} days` : 'Not specified'}</span>
                <div className="turnaround-disclaimer">Production time only, shipping not included.</div>
              </div>
            </div>
            <div className="farm-specialties">
              {farm.specialties.map((spec, idx) => (
                <span key={idx} className="specialty-tag">{spec}</span>
              ))}
            </div>

            <div className="farm-meta">
              {typeof farm.printerCount === 'number' && <span>{farm.printerCount} printers</span>}
              {farm.medicalGradeCapable && <span>Medical grade capable</span>}
            </div>

            <button className="btn-contact" type="button" disabled>
              Ready for Jobs
            </button>
          </div>
        ))}
      </div>
      )}
    </div>
  )
}

